import React, { lazy } from 'react';
import { ShoppingBagIcon, MailCheckIcon, DownloadIcon, FlowerIcon } from 'lucide-react';
import { Reveal } from './Reveal';
const APP_VIDEO = 'https://cdn.magicpatterns.com/uploads/e7pKfYds5FYAzJ6VnwUXBU/Sp0XBWgLLbqp8EBFtMKC_segment_1.mp4';
const steps = [{
  icon: ShoppingBagIcon,
  title: 'Compras tu acceso hoy',
  desc: 'Un solo pago, seguro y sin suscripciones ocultas. Usa el correo con el que quieras acceder.'
}, {
  icon: MailCheckIcon,
  title: 'Tu correo queda habilitado',
  desc: 'El email de la compra es el que te da acceso: con él ingresas para ver tu link de descarga.'
}, {
  icon: DownloadIcon,
  title: 'Descargas la app en 1 minuto',
  desc: 'Ingresas tu correo, ves el link de descarga y la instalas sin complicaciones.'
}, {
  icon: FlowerIcon,
  title: 'Comienzas tu Día 1 hoy mismo',
  desc: 'Tu transformación empieza en cuanto abres la app.'
}];
export function HowItWorksSection() {
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.muted = true;
    const play = () => {
      video.play().catch(() => {
        // Algunos navegadores exigen interacción; el video queda listo con controles.
      });
    };
    const restart = () => {
      video.currentTime = 0;
      play();
    };
    play();
    video.addEventListener('canplay', play);
    video.addEventListener('ended', restart);
    return () => {
      video.removeEventListener('canplay', play);
      video.removeEventListener('ended', restart);
    };
  }, []);
  return <section className="w-full bg-crema py-20 sm:py-28" data-id="element-126" data-mp="mk1o7bevhfs2">
      <div className="mx-auto max-w-6xl px-4 sm:px-6" data-id="element-127" data-mp="rb9ye9o1156a">
        <Reveal className="mx-auto max-w-2xl text-center" data-id="element-128" data-mp="rcuqlsmvcc0z">
          <h2 className="font-serif text-3xl font-semibold text-tinta sm:text-4xl lg:text-5xl" data-id="element-129" data-mp="3q2boiksy44ej">
            Comenzar es más simple de lo que crees
          </h2>
          <p className="mt-4 text-lg text-tinta/70" data-id="element-130" data-mp="3uya9dyf2f4a7">
            Cuatro pasos y en minutos estás dentro, cuidándote. Solo necesitas
            el correo que usaste al comprar.
          </p>
        </Reveal>

        <Reveal className="mx-auto mt-10 max-w-xs sm:max-w-sm" data-id="element-131" data-mp="1aygkde9gsdjf">
          <div className="overflow-hidden rounded-3xl bg-rosa-light/20 shadow-xl shadow-rosa/15 ring-1 ring-white/70" data-id="element-132" data-mp="1axop64js4hto">
            <video ref={videoRef} src={APP_VIDEO} preload="auto" className="h-full w-full bg-crema object-cover" autoPlay muted loop playsInline controls aria-label="Recorrido por la app Vuelve a Vivir" data-id="element-133" data-mp="16z8vjht70b7a" />
          </div>
        </Reveal>

        <ol className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4" data-id="element-134" data-mp="nb03r8vka78b">
          {steps.map((s, i) => <Reveal as="li" key={s.title} delay={i * 0.1} data-id="element-135" data-mp="23oymod2d6avs" data-mpi={i}>
              <div className="relative flex h-full flex-col rounded-3xl bg-white p-7 shadow-sm" data-id="element-136" data-mp="1yn98wpnrbj40">
                <span className="font-serif text-5xl font-semibold text-nude" aria-hidden="true" data-id="element-137" data-mp="2749y3yxa74td">
                  0{i + 1}
                </span>
                <span className="mt-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-rosa/15 text-rosa-dark" data-id="element-138" data-mp="1x86v6qulv25d">
                  <s.icon className="h-6 w-6" strokeWidth={1.6} data-id="element-139" data-mp="2268encvy13hd" />
                </span>
                <h3 className="mt-4 font-serif text-xl font-semibold leading-snug text-tinta" data-id="element-140" data-mp="2pw2pxq0m6jzi">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-tinta/70" data-id="element-141" data-mp="2g611gx4cmzk2">{s.desc}</p>
              </div>
            </Reveal>)}
        </ol>

        <Reveal className="mx-auto mt-10 max-w-2xl" data-id="element-142" data-mp="2l3suxvkn6tqd">
          <p className="glow-pulse-card rounded-2xl border border-rosa/35 bg-white px-5 py-4 text-center text-sm leading-relaxed text-tinta/75" data-id="element-143" data-mp="38uf1fif005y9">
            Importante: el acceso queda vinculado al correo que uses al comprar.
            Con ese mismo email ingresas para ver el link de descarga de la app.
          </p>
        </Reveal>
      </div>
    </section>;
}